import { isEqual } from "lodash";
import { LegacySubscriptionHandler } from "./SubscriptionHandler";
import { GenericDataSet } from "./datasets/GenericDataSet";
import { registerSchemaV2, subscribeSchemaV2 } from "./schemas/ConfigSchema";
import { setConfig } from "./types/Config";
import { inputValidator } from "./utils/validator";
export const subscribersV2 = new Map();
const pendingUnsubscriptions = new Map();
//import order is important due to circular dependencies...project needs refactoring
import { isSocketClosed, notif } from "./RTDNotifV2";
export class SubscriptionHandler extends LegacySubscriptionHandler {
    constructor() {
        super(...arguments);
        this.initialized = false;
    }
    /**
     * This function registers and connects the RTDWC v2 websocket.
     * This is a work in progress and not ready for public consumption.
     */
    async initV2(config) {
        if (this.initialized) {
            throw new Error("[RTDWC-v2] RTDSubscriptionHandler already initialized");
        }
        const validator = inputValidator(registerSchemaV2, config);
        if (!validator.success) {
            this.error = true;
            throw new Error(`[RTDWC-v2] incorrect payload, ${validator.errorMessage}`);
        }
        config.lostConnectionRecoveryTimeout = config.lostConnectionRecoveryTimeout || "120000";
        setConfig({
            ...config,
            rtdHost: new URL("v1", config.apiGatewayHost).href,
            notifUrl: new URL("v1/realtime/subscribe", config.apiGatewayHost).href
        });
        //To be removed after the migration to RTDWC v2 is complete
        this.dataSetFactory = { createDataSet: (type) => new GenericDataSet(type) };
        try {
            notif.init();
        }
        catch (error) {
            this.error = true;
            throw error;
        }
        this.initialized = true;
    }
    /**
     * Websocket based subscription handler for RTDWC v2.
     * This is a work in progress and not ready for public consumption.
     * @function subscribe
     * @param { SubscribeV2 } dataset
     */
    async subscribeV2(dataset) {
        let unsubscribed = false;
        if (this.error || !this.initialized || isSocketClosed) {
            throw new Error("[RTDWC-v2] Websocket initialization not successful");
        }
        const validator = inputValidator(subscribeSchemaV2, dataset);
        if (!validator.success) {
            throw new Error(`[RTDWC-v2] incorrect payload, ${validator.errorMessage}`);
        }
        if (!dataset.error) {
            dataset.error = function () {
                return;
            };
        }
        const { datasetName, update, error, connectionStatus, subscribeParams } = dataset;
        let subscriberDataSet = subscribersV2.get(datasetName);
        if (subscriberDataSet) {
            if (!isEqual(subscriberDataSet.subscribeParams, subscribeParams)) {
                // filters/sorting changed for the same dataset, need to re-subscribe
                await this.forceUnsubscribe(datasetName);
                return this.subscribeV2(dataset);
            }
            else {
                subscriberDataSet.callbacks.push({
                    update,
                    error,
                    connectionStatus
                });
                if (subscriberDataSet.data) {
                    subscriberDataSet.data.datasetValues = [...subscriberDataSet.data.datasetValues];
                    update(subscriberDataSet.data);
                }
                else if (subscriberDataSet.isError && !subscriberDataSet.isInProgress) {
                    await this.callNotifSubscribe(datasetName, subscriberDataSet);
                }
            }
        }
        else {
            subscriberDataSet = {
                data: null,
                callbacks: [
                    {
                        update,
                        error,
                        connectionStatus
                    }
                ],
                subscribeParams,
                isError: false,
                isInProgress: true
            };
            subscribersV2.set(datasetName, subscriberDataSet);
            const dataSet = new GenericDataSet(datasetName);
            dataSet.subscribe((data, delta) => {
                const subs = subscribersV2.get(datasetName);
                if (subs) {
                    subs.data = data;
                    subs.callbacks.forEach((sub) => {
                        data.datasetValues = [...data.datasetValues];
                        if (delta) {
                            delta.datasetValues = [...delta.datasetValues];
                        }
                        sub.update(data, delta);
                    });
                }
            }, (err) => {
                const subs = subscribersV2.get(datasetName);
                if (subs) {
                    subs.callbacks.forEach((sub) => sub.error(err));
                }
            }, (isConnected) => {
                const subs = subscribersV2.get(datasetName);
                if (subs) {
                    subs.callbacks.forEach((sub) => sub.connectionStatus?.(isConnected));
                }
            });
            await this.callNotifSubscribe(datasetName, subscriberDataSet);
        }
        return {
            unsubscribe() {
                if (unsubscribed) {
                    // eslint-disable-next-line no-console
                    console.warn("[RTDWC-v2] Dataset already unsubscribed");
                    return;
                }
                const dataValue = subscribersV2.get(datasetName);
                if (dataValue) {
                    if (dataValue?.callbacks?.length) {
                        dataValue.callbacks = dataValue.callbacks.filter((cb) => cb.update !== dataset.update) || [];
                    }
                    if (dataValue.callbacks?.length === 0) {
                        // unsubscribe to push data when there is not a single subscription from any widget
                        subscribersV2.delete(datasetName);
                        pendingUnsubscriptions.set(datasetName, notif.unsubscribe(datasetName));
                    }
                    unsubscribed = true;
                }
                else {
                    throw new Error("[RTDWC-v2] Dataset not subscribed - dataset not found in cache");
                }
            }
        };
    }
    async callNotifSubscribe(datasetName, subscriberDataSet) {
        const pendingUnsubscription = pendingUnsubscriptions.get(datasetName);
        if (pendingUnsubscription) {
            try {
                await pendingUnsubscription;
            }
            catch (err) {
                // eslint-disable-next-line no-console
                console.error(`[RTDWC-v2] Error while unsubscription ${err} | timestamp=${new Date()}, UTC=${new Date().toUTCString()}`);
            }
            finally {
                pendingUnsubscriptions.delete(datasetName);
            }
        }
        await notif.subscribeV2(datasetName, subscriberDataSet);
    }
    async forceUnsubscribe(datasetName) {
        try {
            await notif.unsubscribe(datasetName);
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.error(`[RTDWC-v2] Error while force unsubscription ${err} | timestamp=${new Date()}, UTC=${new Date().toUTCString()}`);
        }
        finally {
            subscribersV2.delete(datasetName);
        }
    }
    validateMessageType(messageType) {
        if (!messageType || typeof messageType !== "string" || messageType.trim() === "") {
            throw new Error("[RTDWC] messageType must be a non-empty string");
        }
    }
    validateHandler(handler, isOptional = false) {
        if (isOptional && !handler) {
            return; // Optional handler can be undefined
        }
        if (!handler || typeof handler !== "function") {
            const message = isOptional
                ? "[RTDWC] handler must be a function when provided"
                : "[RTDWC] handler must be a function";
            throw new Error(message);
        }
    }
    validateNotifHandlerState() {
        if (this.error || !notif) {
            throw new Error("[RTDWC] WebSocket handler not initialized or in error state");
        }
    }
    /**
     * Executes operation with error handling and logging
     * @private
     */
    executeWithErrorHandling(operation, operationName) {
        try {
            return operation();
        }
        catch (error) {
            // eslint-disable-next-line no-console
            console.error(`[RTDWC] Error ${operationName}: ${error}`);
            throw error;
        }
    }
    /**
     * Register a custom notif handler for specific message types
     * @param messageType The message type to handle (e.g., "Wellness_Break_Handler")
     * @param handler The callback function to execute when this message type is received
     * @throws {Error} When messageType is empty or handler is not a function
     */
    registerCustomNotifHandler(messageType, handler) {
        this.validateMessageType(messageType);
        this.validateHandler(handler);
        this.validateNotifHandlerState();
        this.executeWithErrorHandling(() => notif.registerCustomNotiftHandler(messageType, handler), "registering custom notif handler");
    }
    /**
     * Unregister a custom notif handler for a specific message type
     * @param messageType The message type to stop handling
     * @param handler The specific handler to remove (optional - if not provided, removes all handlers for this type)
     * @throws {Error} When messageType is empty
     */
    unregisterCustomNotifHandler(messageType, handler) {
        this.validateMessageType(messageType);
        this.validateHandler(handler, true);
        this.validateNotifHandlerState();
        this.executeWithErrorHandling(() => notif.unregisterCustomNotifHandler(messageType, handler), "unregistering custom notif handler");
    }
    /**
     * Get all registered custom notif handlers for debugging/inspection
     * @returns {Map<string, CustomNotifHandler[]>} Map of message types to their handlers
     * @throws {Error} When WebSocket handler is not initialized
     */
    getRegisteredNotifHandlers() {
        this.validateNotifHandlerState();
        return this.executeWithErrorHandling(() => notif.getRegisteredNotifHandlers(), "getting registered handlers");
    }
}
